import { useEffect } from 'react';
import { company } from '../data/company';
import { SITE_URL, DEFAULT_OG, absoluteUrl } from '../lib/seo';

/**
 * Dependency-free document-head manager for the public marketing site (same technique as the ERP's
 * lib/seo, kept self-contained here with GreenbayBio branding). Renders nothing; on mount/update it
 * upserts title / description / canonical / robots / Open Graph / Twitter tags and injects per-page
 * JSON-LD (removed on unmount so structured data never leaks between routes). Props mirror the
 * reference UI's <Seo> so ported pages need no changes.
 */
export interface SeoProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: 'website' | 'article' | 'product';
  noIndex?: boolean;
  keywords?: string[];
  jsonLd?: Record<string, unknown>[];
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${CSS.escape(key)}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function Seo({ title, description, path = '/', image = DEFAULT_OG, type = 'website', noIndex = false, keywords, jsonLd = [] }: SeoProps) {
  const jsonLdKey = JSON.stringify(jsonLd);
  useEffect(() => {
    const fullTitle = title.includes(company.name) ? title : `${title} | ${company.name}`;
    const desc = (description ?? '').replace(/\s+/g, ' ').trim().slice(0, 300);
    const pathname = path.split('?')[0];
    const canonical = absoluteUrl(pathname);
    const ogImage = absoluteUrl(image);

    document.title = fullTitle;
    document.documentElement.setAttribute('lang', 'en');
    upsertMeta('name', 'description', desc);
    if (keywords?.length) upsertMeta('name', 'keywords', keywords.join(', '));
    upsertMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow');
    upsertMeta('name', 'googlebot', noIndex ? 'noindex, nofollow' : 'index, follow');
    upsertLink('canonical', canonical);

    upsertMeta('property', 'og:site_name', company.name);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', desc);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:locale', 'en_IN');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', desc);
    upsertMeta('name', 'twitter:image', ogImage);
    upsertMeta('name', 'author', company.legalName);

    const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    const scripts = items.map((item) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-jsonld', '');
      s.text = JSON.stringify(item);
      document.head.appendChild(s);
      return s;
    });
    return () => scripts.forEach((s) => s.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, type, noIndex, jsonLdKey]);

  return null;
}

export const SITE_URL_EXPORT = SITE_URL;
